/**
 * Where an instruction stands, in one operator-facing line. Pure so it is
 * unit-tested without React.
 *
 * Every button in this UI creates an instruction and then *nothing happens on the
 * page it was clicked on* until the host writes something — which for an offline
 * host is never, and for `prepare.start` is many minutes. An operator who cannot
 * see that the click landed clicks again. So each card looks up the newest
 * instruction of its own kind and shows it: queued (and why it is not moving),
 * running (for how long), or how the last one ended.
 */
import {
    instructionLogObject,
    isInflightStatus,
    STORAGE_PREFIX,
    type HostDoc,
    type HostRole,
    type InstructionDoc,
    type InstructionKind,
    type Timestamp,
} from '@finvari/deployer';
import { agoTextCoarse, formatDuration, type HostView } from './hostStatus';

export type InstructionRow = InstructionDoc & { id: string };

/**
 * Where this instruction's archived log lives, once the live chunks are gone.
 *
 * Only when there are none: the chunks expire after 30 days and the host archives
 * every log to the artifacts bucket, so `logChunks === 0` on a finished instruction
 * means "read the object instead". Naming the object while chunks exist would fetch a
 * copy of what is already streaming. An instruction with no release (a host action ran
 * outside one) has no archive to name.
 */
export function archivePathFor(row: InstructionRow, host: HostRole): string | undefined {
    return row.release && row.logChunks === 0
        ? instructionLogObject(STORAGE_PREFIX[host], row.release, row.id)
        : undefined;
}

/**
 * A `pending` instruction against an online host should be claimed within a
 * second or two; past this the controller is probably not listening, which is
 * worth saying rather than showing "queued" forever.
 */
export const UNCLAIMED_WARNING_MS = 30_000;

/** The newest instruction of each kind, from a newest-first list. */
export function latestByKind(rows: InstructionRow[]): Map<InstructionKind, InstructionRow> {
    const out = new Map<InstructionKind, InstructionRow>();
    for (const row of rows) {
        if (!out.has(row.kind)) out.set(row.kind, row);
    }
    return out;
}

/**
 * The newest instruction a card should report on: of its kind, and — when the card
 * names a release — from that release.
 *
 * Without the release test a card outlives its subject. The close card on a freshly
 * opened release found the *previous* release's `release.close` and announced
 * "Succeeded 9h ago" above its own disabled button, which reads as if this release
 * had already closed. A card with no release (`host.sync`, `host.doctor`) belongs to
 * no release and reports the newest of its kind.
 *
 * `match` narrows further, for a kind that covers several actions — `prepare.step`
 * is one kind for five steps.
 */
export function latestForAction(
    rows: InstructionRow[],
    kind: InstructionKind,
    release?: string,
    match?: (row: InstructionRow) => boolean,
): InstructionRow | undefined {
    return rows.find(
        (r) => r.kind === kind && (release === undefined || r.release === release) && (!match || match(r)),
    );
}

/** Instructions still pending or running, oldest first — the order the host runs them. */
export function inflightRows(rows: InstructionRow[]): InstructionRow[] {
    return rows.filter((r) => isInflightStatus(r.status)).reverse();
}

/** Tooltip on a button whose kind already has an instruction in flight. */
export const INFLIGHT_DISABLED_REASON = 'Already queued or running — wait for it to finish.';

export interface ActivityLine {
    severity: 'info' | 'warning' | 'error' | 'success';
    text: string;
    /** True while the instruction is pending/running, so the button that creates this kind should be disabled. */
    inflight: boolean;
}

export interface ActivityHost {
    role: 'prepare' | 'release';
    view: HostView;
    /** From the host's registry entry; undefined while the VM is unprovisioned. */
    expectedBootSeconds?: number;
}

/**
 * A stored timestamp's age, or undefined when the field is not yet resolved —
 * the creating browser sees its own `serverTimestamp()` as null until the server
 * acknowledges the write.
 */
function ageOf(ts: Timestamp | undefined | null, now: number): number | undefined {
    return ts?.toMillis ? Math.max(0, now - ts.toMillis()) : undefined;
}

/**
 * "Succeeded 2m ago". Coarse on purpose: this caption stays up for five minutes
 * after the instruction ended (and indefinitely for a failure), so a seconds
 * figure would animate for the whole time it is being read. A *running*
 * instruction keeps `formatDuration` — there the ticking is the progress report.
 */
function withAge(prefix: string, ageMs: number | undefined): string {
    return ageMs === undefined ? prefix : `${prefix} ${agoTextCoarse(ageMs)}`;
}

/** The one-line status of an instruction for the card that creates its kind. */
export function describeActivity(row: InstructionRow, host: ActivityHost, now: number): ActivityLine {
    const createdAge = ageOf(row.createdAt, now);
    const finishedAge = ageOf(row.finishedAt, now) ?? createdAge;

    switch (row.status) {
        case 'pending':
            return describePending(createdAge, host);
        case 'running': {
            const since = ageOf(row.claimedAt, now) ?? createdAge;
            return {
                severity: 'info',
                inflight: true,
                text: since === undefined ? 'Running.' : `Running for ${formatDuration(since)}.`,
            };
        }
        case 'succeeded':
            return { severity: 'success', inflight: false, text: withAge('Succeeded', finishedAge) + '.' };
        case 'failed':
            return {
                severity: 'error',
                inflight: false,
                text: withAge('Failed', finishedAge) + (row.error ? `: ${row.error}` : '.'),
            };
        case 'rejected':
            return {
                severity: 'warning',
                inflight: false,
                text: withAge('Rejected', finishedAge) + (row.rejectedReason ? `: ${row.rejectedReason}` : '.'),
            };
        case 'cancelled':
            return { severity: 'warning', inflight: false, text: withAge('Cancelled', finishedAge) + '.' };
    }
}

/**
 * A pending instruction is only actionable by the operator when the host is not
 * going to pick it up; say which case this is instead of a bare "queued".
 */
function describePending(createdAge: number | undefined, host: ActivityHost): ActivityLine {
    const queued = withAge('Queued', createdAge);
    const boot = host.expectedBootSeconds ? ` (about ${host.expectedBootSeconds} s)` : '';
    switch (host.view.presentation) {
        // Whether the host will pick this up is exactly what we have not learned
        // yet, so the line says only what is certain: it is queued.
        case 'loading':
            return { severity: 'info', inflight: true, text: `${queued}.` };
        case 'offline':
        case 'unknown':
            return {
                severity: 'warning',
                inflight: true,
                text: `${queued}. The ${host.role} host is offline — start it${boot} and this will run as soon as it is online.`,
            };
        case 'starting':
            return {
                severity: 'info',
                inflight: true,
                text: `${queued}. The ${host.role} host is starting${boot}; this will run once it is online.`,
            };
        case 'stopping':
            return {
                severity: 'warning',
                inflight: true,
                text: `${queued}. The ${host.role} host is stopping; start it again${boot} and this will run when it is back.`,
            };
        case 'online':
            if (createdAge !== undefined && createdAge > UNCLAIMED_WARNING_MS) {
                return {
                    severity: 'warning',
                    inflight: true,
                    text: `${queued} and still not picked up although the ${host.role} host is online. Its controller may not be listening — run a health check from the Hosts page or check the VM.`,
                };
            }
            return { severity: 'info', inflight: true, text: `${queued} — the ${host.role} host is picking it up.` };
    }
}

/**
 * Whether a checkout reset has been asked for and not yet performed, so the card
 * can tell the operator that starting the host is the other half of the action
 * they took. Nothing else on the page says so: the instruction succeeded, the host
 * went down, and from the outside that is indistinguishable from a Shutdown.
 *
 * Answered by comparing the request against the outcome rather than by clock: the
 * request file is on the persistent disk, so a host left off for a week still has
 * one waiting, and the reset a boot performed names the instruction that asked for
 * it. So the hint stands until that boot has happened — including after a boot that
 * failed before reaching the reset, which is exactly when it still needs saying.
 */
export function resetAwaitingStart(doc: HostDoc | undefined, latestClean: InstructionRow | undefined): boolean {
    if (latestClean?.status !== 'succeeded') return false;
    return doc?.checkoutReset?.instructionId !== latestClean.id;
}

/**
 * How long a finished management action (health check, sync, hold, stop) stays on
 * the host card. Long enough for the operator who clicked to read the outcome;
 * short enough that the card is not a permanent history — the Hosts page list is.
 */
export const RECENT_OUTCOME_MS = 5 * 60_000;

/** In flight, or finished recently enough that the person who clicked still wants to see it. */
export function isInflightOrRecent(row: InstructionRow, now: number): boolean {
    if (isInflightStatus(row.status)) return true;
    const finished = ageOf(row.finishedAt, now);
    return finished !== undefined && finished < RECENT_OUTCOME_MS;
}

/**
 * The finished instruction whose outcome a release page should still report, if
 * any: the newest of the given kinds when it ended badly, or ended well recently.
 *
 * The stepper cannot show a failure — the host re-derives its steps from git and
 * files, which only know done/next/pending — so a rejected or failed attempt
 * leaves the page looking as if the step is merely up next. The outcome therefore
 * stays up until a newer attempt of these kinds supersedes it. A success is shown
 * only for `RECENT_OUTCOME_MS`, as on the host card. In-flight rows are reported
 * separately by the caller, so the newest one being in flight means nothing to
 * report here.
 */
export function lastOutcomeToReport(rows: InstructionRow[], kinds: InstructionKind[], now: number): InstructionRow | undefined {
    const latest = rows.find((r) => kinds.includes(r.kind));
    if (!latest || isInflightStatus(latest.status)) return undefined;
    if (latest.status === 'failed' || latest.status === 'rejected') return latest;
    return isInflightOrRecent(latest, now) ? latest : undefined;
}

/** "queued 2m ago" / "started < 1m ago" / "finished 3m ago" — the list row caption. */
export function timelineText(row: InstructionRow, now: number): string | undefined {
    const finished = ageOf(row.finishedAt, now);
    if (finished !== undefined) return 'finished ' + agoTextCoarse(finished);
    const started = ageOf(row.claimedAt, now);
    if (started !== undefined) return 'started ' + agoTextCoarse(started);
    const created = ageOf(row.createdAt, now);
    return created === undefined ? undefined : 'queued ' + agoTextCoarse(created);
}
